import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import * as Checkbox from '@radix-ui/react-checkbox';
import { CheckIcon } from '@radix-ui/react-icons';
import styles from './AddIpo.module.css';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import Button from '../../components/common/Button/Button';
import { REACT_APP_API_BASE_URL } from '../../apiurl';
import { getAllNcds, createNcd, updateNcd, deleteNcd, uploadNcdMedia } from '../../services/ncdService';
import { validateInvestmentForm } from './validation';
import FormField from './FormField';
import LogoField from './LogoField';
import { formatAdminDate } from '../../utils/datetime';

// Icons
const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const initialFormData = {
    company_name: '',
    logo: '',
    start_date: '',
    close_date: '',
    listing_date: '',
    min_investment: '',
    max_investment: '',
    maturity_amount: '',
    face_value: '',
    coupon_rate: '',
    payment_frequency: '',
    rating: '',
    notes: '',
    company_website: '',
    active_status: true,
    accept_applications: true
};

const NcdPage = () => {
    // List State
    const [ncds, setNcds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal & Form State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [proposalFiles, setProposalFiles] = useState([]);
    
    // Media Upload State
    const [newMediaLabel, setNewMediaLabel] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    // Delete confirmation State
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const baselineRef = useRef('');

    useEffect(() => {
        fetchNcds();
    }, []);

    const fetchNcds = async () => {
        setLoading(true);
        try {
            const res = await getAllNcds();
            const data = res?.data || [];
            setNcds(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(err?.message || "Failed to fetch NCDs");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredNcds = ncds.filter(ncd => 
        ncd.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // --- Form Handlers ---
    
    const openAddModal = () => {
        setIsEditing(false);
        setEditId(null);
        setFormData(initialFormData);
        setErrors({});
        setProposalFiles([]);
        baselineRef.current = JSON.stringify({ fd: initialFormData, files: [] });
        setIsModalOpen(true);
    };

    const openEditModal = (ncd) => {
        setIsEditing(true);
        setEditId(ncd.id);
        
        const formatDate = (dateString) => {
            if (!dateString) return '';
            const d = new Date(dateString);
            return isNaN(d) ? '' : d.toISOString().split('T')[0];
        };

        const fd = {
            company_name: ncd.company_name || '',
            logo: ncd.logo || '',
            start_date: formatDate(ncd.start_date),
            close_date: formatDate(ncd.close_date),
            listing_date: formatDate(ncd.listing_date),
            min_investment: ncd.min_investment ?? '',
            max_investment: ncd.max_investment ?? '',
            maturity_amount: ncd.maturity_amount ?? '',
            face_value: ncd.face_value ?? '',
            coupon_rate: ncd.coupon_rate ?? '',
            payment_frequency: ncd.payment_frequency || '',
            rating: ncd.rating || '',
            notes: ncd.notes || '',
            company_website: ncd.company_website || '',
            active_status: ncd.active_status !== false,
            accept_applications: ncd.accept_applications !== false,
        };
        
        const files = Array.isArray(ncd.proposal_files) ? ncd.proposal_files : [];
        
        setFormData(fd);
        setErrors({});
        setProposalFiles(files);
        baselineRef.current = JSON.stringify({ fd, files });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewMediaLabel('');
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear this field's message as soon as it is edited; the rest stay
        // until the next save so the user still sees what is outstanding.
        setErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
    };

    const isDirty = JSON.stringify({ fd: formData, files: proposalFiles }) !== baselineRef.current;
    
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        const validationErrors = validateInvestmentForm('NCD', formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error("Please correct the highlighted fields");
            return;
        }
        setErrors({});
        if (saving) return;

        const payload = {
            ...formData,
            proposal_files: proposalFiles
        };

        setSaving(true);
        try {
            if (isEditing) {
                await updateNcd(editId, payload);
                toast.success("NCD updated successfully");
            } else {
                await createNcd(payload);
                toast.success("NCD created successfully");
            }
            closeModal();
            fetchNcds();
        } catch (err) {
            toast.error(err?.message || "Failed to save NCD");
        } finally {
            setSaving(false);
        }
    };

    // Opens the confirm dialog; the delete itself runs from confirmDelete.
    const handleDelete = (id) => setDeleteId(id);

    const confirmDelete = async () => {
        if (deleteId == null) return;
        setDeleting(true);
        try {
            await deleteNcd(deleteId);
            toast.success("NCD deleted");
            setDeleteId(null);
            fetchNcds();
        } catch (err) {
            toast.error(err?.message || "Failed to delete NCD");
        } finally {
            setDeleting(false);
        }
    };

    // --- Media Handlers ---
    
    const MAX_FILE_SIZE = 20 * 1024 * 1024;

    const handleMediaUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !newMediaLabel.trim()) return;

        if (file.size > MAX_FILE_SIZE) {
            toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB.`);
            e.target.value = "";
            return;
        }

        const fd = new FormData();
        fd.append("file", file);
        
        setUploading(true);
        try {
            const res = await uploadNcdMedia(fd);
            const { url, type } = res?.data || {};
            
            if (!url) {
                toast.error("Upload failed - no URL returned");
                return;
            }

            setProposalFiles(prev => [...prev, {
                label: newMediaLabel.trim(),
                url,
                type: type || (file.type.includes('image') ? 'image' : 'pdf')
            }]);
            
            toast.success("File uploaded successfully");
            setNewMediaLabel('');
        } catch (err) {
            toast.error(err?.message || "File upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    // Uploads a logo through the same media endpoint and stores the returned
    // path in formData.logo, so the field can still be typed if preferred.
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE) {
            toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB.`);
            e.target.value = "";
            return;
        }

        const fd = new FormData();
        fd.append("file", file);

        setUploadingLogo(true);
        try {
            const res = await uploadNcdMedia(fd);
            const { url } = res?.data || {};
            if (!url) {
                toast.error("Logo upload failed - no URL returned");
                return;
            }
            setFormData(prev => ({ ...prev, logo: url }));
            setErrors(prev => ({ ...prev, logo: undefined }));
            toast.success("Logo uploaded");
        } catch (err) {
            toast.error(err?.message || "Logo upload failed");
        } finally {
            setUploadingLogo(false);
            e.target.value = "";
        }
    };

    const removeMedia = (index) => {
        setProposalFiles(prev => prev.filter((_, i) => i !== index));
    };

    const getPreviewUrl = (url) => {
        const v = String(url || '').trim();
        if (!v) return '';
        if (/^https?:\/\//i.test(v)) return v;
        return `${REACT_APP_API_BASE_URL}${v.replace(/^\//, '')}`;
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>NCD Management</h1>
                <Button onClick={openAddModal}>Add New NCD</Button>
            </div>

            <div className={styles.controlsRow}>
                <div className={styles.searchBox}>
                    <input 
                        type="text" 
                        placeholder="Search by Company Name..." 
                        value={searchTerm}
                        onChange={handleSearch}
                        className={styles.searchInput}
                    />
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Company Name</th>
                            <th>Investment Range</th>
                            <th>Coupon Rate</th>
                            <th>Rating</th>
                            <th>Open Date</th>
                            <th>Close Date</th>
                            <th>Status</th>
                            <th>Files</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredNcds.map(ncd => (
                            <tr key={ncd.id}>
                                <td>{ncd.company_name}</td>
                                <td>{ncd.min_investment} - {ncd.max_investment}</td>
                                <td>{ncd.coupon_rate}%</td>
                                <td>{ncd.rating || '-'}</td>
                                <td>{formatAdminDate(ncd.start_date, '-')}</td>
                                <td>{formatAdminDate(ncd.close_date, '-')}</td>
                                <td>
                                    <span className={`${styles.badge} ${ncd.active_status ? styles.active : styles.inactive}`}>
                                        {ncd.active_status ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>{Array.isArray(ncd.proposal_files) ? ncd.proposal_files.length : 0}</td>
                                <td>
                                    <div className={styles.actions}>
                                        <button className={styles.actionBtn} onClick={() => openEditModal(ncd)} title="Edit">
                                            <EditIcon />
                                        </button>
                                        <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => handleDelete(ncd.id)} title="Delete">
                                            <TrashIcon />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filteredNcds.length === 0 && (
                            <tr>
                                <td colSpan="9" style={{textAlign: 'center', padding: '30px'}}>
                                    {loading ? 'Loading...' : 'No NCDs found'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <FullscreenModal
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    title={isEditing ? "Edit NCD" : "Add NCD"}
                    size="fullscreen"
                    maxWidth="1000px"
                    contentHeight="82vh"
                    disableBackdropClick={true}
                >
                    <div className={styles.modalContent}>
                        <div className={styles.formGrid}>
                            <FormField label="Company Name" name="company_name" required value={formData.company_name} onChange={handleChange} error={errors.company_name} />
                            <LogoField value={formData.logo} onChange={handleChange} error={errors.logo} uploading={uploadingLogo} onUpload={handleLogoUpload} previewUrl={getPreviewUrl(formData.logo)} />
                            <FormField label="Start date" name="start_date" type="date" value={formData.start_date} onChange={handleChange} error={errors.start_date} />
                            <FormField label="Close date" name="close_date" type="date" value={formData.close_date} onChange={handleChange} error={errors.close_date} />
                            <FormField label="Listing date" name="listing_date" type="date" value={formData.listing_date} onChange={handleChange} error={errors.listing_date} />
                            <FormField label="Min investment" name="min_investment" type="number" step="0.01" value={formData.min_investment} onChange={handleChange} error={errors.min_investment} />
                            <FormField label="Max investment" name="max_investment" type="number" step="0.01" value={formData.max_investment} onChange={handleChange} error={errors.max_investment} />
                            <FormField label="Maturity amount" name="maturity_amount" type="number" step="0.01" value={formData.maturity_amount} onChange={handleChange} error={errors.maturity_amount} />
                            <FormField label="Face value" name="face_value" type="number" step="0.01" value={formData.face_value} onChange={handleChange} error={errors.face_value} />
                            <FormField label="Coupon rate (%)" name="coupon_rate" type="number" step="0.01" value={formData.coupon_rate} onChange={handleChange} error={errors.coupon_rate} />
                            <FormField
                                label="Payment frequency"
                                name="payment_frequency"
                                value={formData.payment_frequency}
                                onChange={handleChange}
                                error={errors.payment_frequency}
                                options={[
                                    { value: '', label: 'Select frequency' },
                                    { value: 'Monthly', label: 'Monthly' },
                                    { value: 'Quarterly', label: 'Quarterly' },
                                    { value: 'Semi-Annually', label: 'Semi-annually' },
                                    { value: 'Yearly', label: 'Yearly' },
                                    { value: 'Cumulative', label: 'Cumulative' },
                                ]}
                            />
                            <FormField label="Rating" name="rating" value={formData.rating} onChange={handleChange} error={errors.rating} placeholder="e.g. Crisil AA+" />
                            <FormField label="Company website" name="company_website" value={formData.company_website} onChange={handleChange} error={errors.company_website} placeholder="https://…" />
                            <FormField label="Notes" name="notes" textarea fullWidth value={formData.notes} onChange={handleChange} error={errors.notes} />
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Active status</label>
                                <div className={styles.checkboxRow}>
                                    <Checkbox.Root
                                        className={styles.checkboxRoot}
                                        checked={formData.active_status}
                                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active_status: !!checked }))}
                                    >
                                        <Checkbox.Indicator className={styles.checkboxIndicator}>
                                            <CheckIcon />
                                        </Checkbox.Indicator>
                                    </Checkbox.Root>
                                    <span className={styles.checkboxText}>{formData.active_status ? 'Active' : 'Inactive'}</span>
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Accept applications</label>
                                <div className={styles.checkboxRow}>
                                    <Checkbox.Root
                                        className={styles.checkboxRoot}
                                        checked={formData.accept_applications}
                                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, accept_applications: !!checked }))}
                                    >
                                        <Checkbox.Indicator className={styles.checkboxIndicator}>
                                            <CheckIcon />
                                        </Checkbox.Indicator>
                                    </Checkbox.Root>
                                    <span className={styles.checkboxText}>{formData.accept_applications ? 'Accepting' : 'Not accepting'}</span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.mediaSection}>
                            <h3 className={styles.sectionTitle}>Proposal Files</h3>

                            <div className={styles.addMediaRow}>
                                <div className={styles.formGroup} style={{flex: '0 0 320px'}}>
                                    <label className={styles.label}>Media label</label>
                                    <input 
                                        type="text" 
                                        value={newMediaLabel} 
                                        onChange={(e) => setNewMediaLabel(e.target.value)} 
                                        className={styles.input} 
                                        placeholder="Enter label name (e.g. Prospectus, Form)"
                                    />
                                </div>
                                <div className={styles.uploadGroup}>
                                    <span className={styles.groupLabel}>Upload</span>
                                    <div className={styles.groupBtns}>
                                        <label className={`${styles.uploadBtn} ${!newMediaLabel.trim() ? styles.uploadBtnDisabled : ''}`}>
                                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.81825 1.18188C7.64251 1.00615 7.35759 1.00615 7.18185 1.18188L4.18185 4.18188C4.00611 4.35762 4.00611 4.64254 4.18185 4.81828C4.35759 4.99401 4.64251 4.99401 4.81825 4.81828L7.05005 2.58648V9.49996C7.05005 9.74849 7.25152 9.94996 7.50005 9.94996C7.74858 9.94996 7.95005 9.74849 7.95005 9.49996V2.58648L10.1819 4.81828C10.3576 4.99401 10.6425 4.99401 10.8182 4.81828C10.994 4.64254 10.994 4.35762 10.8182 4.18188L7.81825 1.18188ZM2.5 9.99997C2.77614 9.99997 3 10.2238 3 10.5V12C3 12.5538 3.44565 13 3.99635 13H11.0012C11.5529 13 12 12.5528 12 12V10.5C12 10.2238 12.2239 9.99997 12.5 9.99997C12.7761 9.99997 13 10.2238 13 10.5V12C13 13.104 12.1062 14 11.0012 14H3.99635C2.89019 14 2 13.103 2 12V10.5C2 10.2238 2.22386 9.99997 2.5 9.99997Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
                                            {uploading ? 'Uploading…' : 'Upload Media'}
                                            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" onChange={handleMediaUpload} disabled={uploading || !newMediaLabel.trim()} hidden />
                                        </label>
                                    </div>
                                </div>
                            </div>
                            
                            <div className={styles.mediaList}>
                                {proposalFiles.map((file, idx) => (
                                    <div key={idx} className={styles.mediaItem}>
                                        <div className={styles.mediaPreview}>
                                            {file.type === 'pdf' || file.url?.endsWith('.pdf') ? (
                                                <span className={styles.mediaPreviewIcon}>📄</span>
                                            ) : (
                                                <img src={getPreviewUrl(file.url)} alt={file.label} onError={(e) => e.target.style.display='none'} />
                                            )}
                                        </div>
                                        <div className={styles.mediaInfo}>
                                            <div className={styles.mediaLabel}>{file.label}</div>
                                            <div className={styles.mediaUrl}>{file.url}</div>
                                        </div>
                                        <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => removeMedia(idx)} title="Remove">
                                            <TrashIcon />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.modalActions}>
                            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
                            <Button variant="primary" onClick={handleSave} disabled={!isDirty || saving}>
                                {saving ? 'Saving...' : 'Save NCD'}
                            </Button>
                        </div>
                    </div>
                </FullscreenModal>
            )}

            {deleteId !== null && (
                <div
                    className={styles.confirmOverlay}
                    onClick={() => { if (!deleting) setDeleteId(null); }}
                >
                    <div className={styles.confirmCard} onClick={(e) => e.stopPropagation()}>
                        <h2 className={styles.confirmTitle}>Delete NCD</h2>
                        <p className={styles.confirmText}>
                            Are you sure you want to delete this NCD? This action cannot be undone.
                        </p>
                        <div className={styles.confirmActions}>
                            <Button variant="secondary" onClick={() => setDeleteId(null)} disabled={deleting}>
                                Cancel
                            </Button>
                            <Button variant="danger" onClick={confirmDelete} loading={deleting} disabled={deleting}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NcdPage;
